import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Loader2, Info, Plus, Edit3, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const USER_TYPES = ["me_pilot", "pilot_host", "subscribed_host", "investor", "advocate", "team"];
const PORTALS = ["me", "host", "advocate", "investor", "team", "admin"];
const CATEGORIES = ["makeup", "hair", "lashes", "nails", "culinary", "wellness", "travel", "style", "dispensary", "events", "instructors", "style"];

export default function AdminFaqPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});

  const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/faq`, { headers: auth() });
      if (r.ok) setItems((await r.json()).items || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startNew = () => {
    setCreating(true);
    setDraft({ question: "", answer: "", target_user_types: [], target_categories: [], target_portals: [], linked_cta_label: "", linked_cta_url: "", sort_order: 100 });
  };
  const startEdit = (f) => {
    setEditingId(f.faq_id);
    setDraft({ ...f });
  };

  const save = async () => {
    const method = creating ? "POST" : "PUT";
    const url = creating ? `${API}/admin/faq` : `${API}/admin/faq/${editingId}`;
    const r = await fetch(url, {
      method,
      headers: { ...auth(), "Content-Type": "application/json" },
      body: JSON.stringify(draft),
    });
    if (r.ok) {
      toast.success("Saved");
      setCreating(false); setEditingId(null); setDraft({}); load();
    } else toast.error("Save failed");
  };

  const remove = async (fid) => {
    if (!window.confirm("Delete?")) return;
    const r = await fetch(`${API}/admin/faq/${fid}`, { method: "DELETE", headers: auth() });
    if (r.ok) { toast.success("Deleted"); load(); }
  };

  const toggleTag = (field, value) => {
    setDraft((d) => {
      const arr = d[field] || [];
      return { ...d, [field]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value] };
    });
  };

  const editing = creating || editingId;

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto" data-testid="admin-faq-page">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold flex items-center gap-2"><Info className="w-5 h-5" /> Help / FAQ</h1>
        {!editing && <Button onClick={startNew} data-testid="faq-new-btn"><Plus className="w-4 h-4 mr-1" /> New FAQ</Button>}
      </div>

      {editing && (
        <Card className="mb-4 border-amber-300 bg-amber-50">
          <CardContent className="p-4 space-y-3">
            <h3 className="text-sm font-semibold">{creating ? "New FAQ" : "Edit FAQ"}</h3>
            <Input placeholder="Question" value={draft.question || ""} onChange={e => setDraft({...draft, question: e.target.value})} data-testid="faq-question" />
            <Textarea rows={4} placeholder="Answer" value={draft.answer || ""} onChange={e => setDraft({...draft, answer: e.target.value})} data-testid="faq-answer" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input placeholder="Linked CTA label (optional)" value={draft.linked_cta_label || ""} onChange={e => setDraft({...draft, linked_cta_label: e.target.value})} data-testid="faq-cta-label" />
              <Input placeholder="Linked CTA URL" value={draft.linked_cta_url || ""} onChange={e => setDraft({...draft, linked_cta_url: e.target.value})} data-testid="faq-cta-url" />
            </div>
            <TargetingGrid label="Show to user types" values={draft.target_user_types} options={USER_TYPES} onToggle={(v) => toggleTag("target_user_types", v)} testidPrefix="faq-ut" />
            <TargetingGrid label="Show for categories" values={draft.target_categories} options={CATEGORIES} onToggle={(v) => toggleTag("target_categories", v)} testidPrefix="faq-cat" />
            <TargetingGrid label="Show in portals" values={draft.target_portals} options={PORTALS} onToggle={(v) => toggleTag("target_portals", v)} testidPrefix="faq-portal" />
            <div className="flex gap-2">
              <Button onClick={save} data-testid="faq-save"><Save className="w-3.5 h-3.5 mr-1" /> Save</Button>
              <Button variant="outline" onClick={() => { setCreating(false); setEditingId(null); setDraft({}); }}>Cancel</Button>
            </div>
            <p className="text-[11px] text-gray-500">Empty arrays = shown everywhere (no filter). Populate to restrict visibility.</p>
          </CardContent>
        </Card>
      )}

      {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto my-10 text-gray-400" /> : (
        <Card>
          <CardContent className="p-2">
            {items.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">No FAQ items yet.</p>
            ) : items.map(f => (
              <div key={f.faq_id} className="flex items-start gap-3 p-3 border-b last:border-b-0" data-testid={`faq-row-${f.faq_id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">{f.question}</p>
                  <p className="text-xs text-gray-500 line-clamp-2">{f.answer}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(f.target_user_types || []).map(t => <Badge key={t} className="text-[10px] bg-indigo-50 text-indigo-700 border-0">{t}</Badge>)}
                    {(f.target_categories || []).map(t => <Badge key={t} className="text-[10px] bg-pink-50 text-pink-700 border-0">{t}</Badge>)}
                    {(f.target_portals || []).map(t => <Badge key={t} className="text-[10px] bg-green-50 text-green-700 border-0">{t}</Badge>)}
                  </div>
                </div>
                <Button size="icon" variant="ghost" onClick={() => startEdit(f)}><Edit3 className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => remove(f.faq_id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export function TargetingGrid({ label, values = [], options, onToggle, testidPrefix }) {
  return (
    <div>
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {options.map(o => {
          const on = (values || []).includes(o);
          return (
            <button
              key={o}
              type="button"
              onClick={() => onToggle(o)}
              className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${on ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}
              data-testid={`${testidPrefix}-${o}`}
            >
              {o}
            </button>
          );
        })}
      </div>
    </div>
  );
}
